import React from "react";
import * as QrCode from "qrcode.react";
import { router } from "dva";

import { Form, Input, Button, Checkbox, Typography, Modal, message, Divider, Row, Col } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { template, Validators, formatDateTimeSend, crypt, decrypt } from "@reco-m/core";
import { ViewComponent, mtaH5Click, statisticsEvent, getParkCode, Countdown } from "@reco-w/core-ui";

import { Namespaces, getRandomKey, loginModel } from "@reco-w/auth-models";

export namespace LoginCommon {
    export interface IProps<S extends IState = IState> extends ViewComponent.IProps<S> {
        /**
         * 成功后回调
         */
        onSuccess?: () => void;
        /**
         * 是否是模态框
         */
        isModal?: boolean;
    }

    export interface IState extends ViewComponent.IState, loginModel.StateType {
        visible?: boolean;
    }

    export class Component<P extends IProps = IProps, S extends IState = IState> extends ViewComponent.Base<P, S> {
        namespace = Namespaces.login;
        timer;
        /**
         * true是账号登录，false是二维码
         */
        showMore = true;
        /**
         * 二维码更新时间，单位：秒
         */
        dimTime = 300;
        /**
         * 是否在检查二维码过期
         */
        isCheckQR = true;
        /**
         * 是否点击转换成扫码
         */
        isClickSwitchScan = false;
        /**
         * 扫码起始时间判断
         */
        timeStart;

        componentDidMount(): void {
            this.dispatch({ type: "initPage" });
            this.getRememberLoginName();
        }

        componentWillUnmount(): void {
            this.dispatch({ type: "input", data: { password: "", loginName: "", showMore: true, isMobileLogin: false, phone: null, authcode: null } });
            this.timer && clearTimeout(this.timer);
        }

        componentReceiveProps(_next) {
            if (!this.showMore && !this.timer && this.isCheckQR)
                this.timer = setInterval(() => {
                    this.checkTime();
                }, 2000);
        }

        /**
         * 获取记住的用户名密码
         */
        getRememberLoginName() {
            let loginName = "",
                password = "";
            if (this.getCookie("username")) {
                loginName = this.getCookie("username");
                password = decrypt(this.getCookie("userpwd"));
            }

            this.dispatch({ type: "input", data: { loginName, password } });
        }

        /**
         * 点击"记住密码"时
         * @param checked 是否选中
         */
        onChange(checked: boolean) {
            this.dispatch({ type: "input", data: { rememberPSW: checked } });
            if (checked) {
                mtaH5Click(statisticsEvent.rememberPassword);
                this.delCookie("username");
                this.delCookie("userpwd");
            }
        }

        /**
         * 账密登录时的校验
         */
        validator() {
            const { required, composeControl, ValidatorControl } = Validators,
                { state } = this.props;
            return ValidatorControl(
                composeControl([required], { value: state!.loginName, name: "账号或手机号" }),
                composeControl([required], { value: state!.password, name: "密码" })
            );
        }

        /**
         * 立即登录
         */
        login() {
            const { state, onSuccess, isModal } = this.props,
                rememberPSW = state!.rememberPSW,
                loginName = state!.loginName,
                password = state!.password;
            const msg = this.validator()!();
            if (msg) {
                message.error(msg.join());
                return;
            }

            this.dispatch({
                type: "submit",
                isModal: isModal,
                callback: () => {
                    message.success("登录成功");
                    if (onSuccess) {
                        onSuccess();
                    }
                },
                message: this.message,
            });
            this.delCookie("username");
            this.delCookie("userpwd");
            if (rememberPSW) {
                this.setCookie("username", loginName, 1000);
                this.setCookie("userpwd", crypt(password), 1000);
            }
        }

        /**
         * 设置cookie
         * @param name cookie的名称
         * @param value cookie的值
         * @param day 设置天数
         */
        setCookie(name, value, day) {
            let date = new Date();
            date.setDate(date.getDate() + day);
            document.cookie = name + "=" + value + ";expires=" + date;
        }

        /**
         * 获取cookie
         * @param name cookie的名称
         */
        getCookie(name) {
            let reg = RegExp(name + "=([^;]+)");
            let arr = document.cookie.match(reg);
            if (arr) {
                return arr[1];
            } else {
                return "";
            }
        }

        /**
         * 删除cookie
         * @param name cookie的名称
         */
        delCookie(name) {
            this.setCookie(name, null, -1);
        }

        /**
         * 切换登录方式
         * @param e true为账密登录，false为扫码登录
         */
        loginSwitch(e: boolean) {
            this.dispatch({ type: "input", data: { showMore: e } });
            this.showMore = e;
            if (e) {
                mtaH5Click(statisticsEvent.selectAccountLogin);
                this.timer && clearTimeout(this.timer);
                this.timer = undefined;
            } else {
                this.isClickSwitchScan = true;
                this.dispatch({ type: "input", data: { QRCodeKey: getRandomKey() } });
                mtaH5Click(statisticsEvent.selectScanLogin);
            }
        }

        /**
         * 判断二维码是否过期
         */
        checkTime() {
            let { state } = this.props,
                QRCodeKey = state!.QRCodeKey;

            if (this.isClickSwitchScan) {
                this.timeStart = new Date();
                this.isClickSwitchScan = false;
            }

            let timeEnd: any = new Date(),
                dim = timeEnd - this.timeStart,
                dimTime = Math.ceil(dim / 1000);

            if (dimTime >= this.dimTime) {
                this.dispatch({ type: "input", data: { QRCodeKey: "二维码已过期" } });
                Modal.confirm({
                    title: "提示",
                    content: "二维码已过期，请刷新后重新扫描",
                    onOk: () => {
                        window.location.reload();
                    },
                    onCancel() {},
                    cancelText: "取消",
                    okText: "确定",
                });
                this.timer && clearTimeout(this.timer);
                this.timer = undefined;
                this.isCheckQR = false;
            } else {
                this.dispatch({
                    type: "checkQRCodeBindUser",
                    data: { code: QRCodeKey, timeStart: formatDateTimeSend(this.timeStart), dimTime: this.dimTime },
                });
            }
        }

        /**
         * 渲染登录栏
         * @returns login content box
         */
        renderLoginContentBox(): React.ReactNode {
            return (
                <div className="login-content-box">
                    {/* {this.renderLoginTitleRight()} */}
                    {this.renderLoginTitleContext()}
                    {this.renderLoginForm()}
                </div>
            );
        }

        /**
         * 渲染登录栏抬头右侧
         * @returns login title right
         */
        renderLoginTitleRight(): React.ReactNode {
            const { state } = this.props,
                isMobileLogin = state!.isMobileLogin,
                showMore = state!.showMore;

            return (
                <div className={"erwe-login " + (!showMore ? "erwe-login-erwe" : "")}>
                    <div className="erwe-icon" onClick={() => this.loginSwitch(!showMore)}>
                        <i className={"icon " + (!showMore ? "icondiannao " : "iconbarcode-qr")}></i>
                    </div>
                    <div className="login-tip">
                        <div className="poptip">
                            <div className="poptip-arrow">
                                <em></em>
                                <span></span>
                            </div>
                            <div className="poptip-content">{showMore ? "扫码登录" : isMobileLogin ? "验证码登录" : "账号登录"}</div>
                        </div>
                    </div>
                </div>
            );
        }

        /**
         * 渲染登录栏抬头文字
         * @returns
         */
        renderLoginTitleContext(): React.ReactNode {
            const { state } = this.props,
                isMobileLogin = state!.isMobileLogin,
                showMore = state!.showMore;

            return <Typography.Title level={3}>{showMore ? (isMobileLogin ? "验证码登录" : "账号密码登录") : "扫码登录"}</Typography.Title>;
        }

        /**
         * 渲染登录填写内容
         * @returns login form
         */
        renderLoginForm(): React.ReactNode {
            const { state } = this.props,
                isMobileLogin = state!.isMobileLogin,
                showMore = state!.showMore;
            return showMore ? (isMobileLogin ? this.renderMobileLogin() : this.renderCommonLogin()) : this.renderQRCodeLogin();
        }

        /**
         * 渲染扫码登录
         */
        renderQRCodeLogin(): React.ReactNode {
            const { state } = this.props,
                QRCodeKey = state!.QRCodeKey;

            return (
                <Form className="login-form">
                    <div>
                        <div className="mt15" style={{ textAlign: "center" }}>
                            {QRCodeKey && <QrCode size={144} value={`${server["appdownURL"]}?weblogincode=${QRCodeKey}`} />}
                        </div>
                        <div className="login-statu">
                            <i className="icon iconsaoma size-29"></i>
                            <div className="login-statu-text">
                                <span>打开App</span>
                                <span>扫一扫登录</span>
                            </div>
                        </div>
                    </div>
                    <div style={{ height: "30px" }}>
                        <router.Link to={`/${getParkCode()}/login/register`} className="pull-right">
                            注册
                        </router.Link>
                        <Divider type="vertical" />
                        <a onClick={() => this.setState({ visible: true })} className="pull-right mr15">
                            下载 App
                        </a>
                    </div>
                    <Modal
                        title="打开手机扫一扫即可下载客户端"
                        visible={this.state!.visible}
                        onCancel={() => this.setState({ visible: false })}
                        footer={null}
                        style={{ top: -100 }}
                        width={380}
                        centered
                    >
                        <QrCode size={347} value={server["appdownURL"]} />
                    </Modal>
                </Form>
            );
        }

        /**
         * 修改是否是验证码登录
         */
        onChangeIsMobile(isMobileLogin) {
            this.dispatch({ type: "input", data: { isMobileLogin, phone: null, authcode: null } });
        }

        /**
         * 渲染账密登录
         */
        renderCommonLogin(): React.ReactNode {
            let { state } = this.props;
            const rememberPSW = state!.rememberPSW;
            let loginName = state!.loginName,
                password = state!.password;
            const isLogin = state!.isLogin;

            return (
                <Form className="login-form mt30">
                    <Form.Item>
                        <Input
                            value={loginName}
                            onChange={(e) => {
                                this.dispatch({ type: "input", data: { loginName: e.target.value } });
                            }}
                            prefix={<UserOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                            placeholder="请输入您的账号或手机号"
                        />
                    </Form.Item>
                    <Form.Item>
                        <Input
                            value={password}
                            onChange={(e) => this.dispatch({ type: "input", data: { password: e.target.value } })}
                            prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                            type="password"
                            placeholder="请输入您的密码"
                        />
                    </Form.Item>
                    <Form.Item>
                        <Checkbox checked={rememberPSW} onChange={(e) => this.onChange(e.target.checked)}>
                            记住密码
                        </Checkbox>
                        <router.Link to={`/${getParkCode()}/login/password`} className="login-form-forgot">
                            忘记密码
                        </router.Link>
                        <Button loading={isLogin} type="primary" htmlType="submit" className="login-form-button" onClick={this.login.bind(this)}>
                            立即登录
                        </Button>
                        <div className="mt5">
                            <router.Link to={`/${getParkCode()}/login/register`}>注册新用户</router.Link>
                            <a className="login-form-forgot" onClick={() => this.onChangeIsMobile(true)}>
                                验证码登录
                            </a>
                        </div>
                    </Form.Item>
                </Form>
            );
        }

        /**
         * 校验手机号
         */
        phoneValidator() {
            const { cellphone, required, composeControl } = Validators,
                { state } = this.props;

            return composeControl([required, cellphone], { value: state!.phone, name: "手机号码" });
        }

        /**
         * 验证码发送前校验 及 发送
         * @param delay 延迟时间方法
         */
        sendVerifyCode(delay: Function) {
            const msg = this.phoneValidator()!();

            if (msg) {
                this.message.error(msg.msg.join());
                return;
            }

            const { state } = this.props;

            this.dispatch({
                type: `sendVerifyCode`,
                data: { username: state!.phone },
                delay,
            });
        }

        /**
         * 校验手机
         * @returns
         */
        validatePhone(): boolean {
            const msg = this.phoneValidator()!();

            if (msg) {
                return true;
            }

            return false;
        }

        /**
         * 验证码登录
         */
        mobileLogin() {
            const { onSuccess, isModal } = this.props;
            this.dispatch({
                type: "smsLogin",
                message: this.message,
                isModal,
                callback: () => {
                    message.success("登录成功");
                    if (onSuccess) {
                        onSuccess();
                    }
                },
            });
        }

        /**
         * 渲染手机登录
         */
        renderMobileLogin(): React.ReactNode {
            let { state } = this.props;
            let phone = state!.phone,
                authcode = state!.authcode;
            const isLogin = state!.isLogin;

            return (
                <Form className="login-form mt30">
                    <Form.Item>
                        <Input
                            value={phone}
                            onChange={(e) => {
                                this.dispatch({ type: "input", data: { phone: e.target.value } });
                            }}
                            prefix={<UserOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                            placeholder="请输入您的手机号"
                            autoComplete="new-password" // 阻止浏览器在password类型的input中自动赋值
                        />
                    </Form.Item>
                    <Form.Item>
                        <Row gutter={8}>
                            <Col span={16}>
                                <Input
                                    value={authcode}
                                    onChange={(e) => this.dispatch({ type: "input", data: { authcode: e.target.value } })}
                                    prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                                    placeholder="请输入验证码"
                                    autoComplete="new-password" // 阻止浏览器在password类型的input中自动赋值
                                />
                            </Col>
                            <Countdown.Component
                                className="size-14 grayColor3"
                                start={this.sendVerifyCode.bind(this)}
                                content="发送验证码"
                                isButton={true}
                                buttonType="primary"
                                disabled={this.validatePhone()}
                                isUseImageVertify={true}
                            />
                        </Row>
                    </Form.Item>
                    <Form.Item>
                        <div className="blank22" />

                        <Button loading={isLogin} type="primary" htmlType="submit" className="login-form-button" onClick={() => this.mobileLogin()}>
                            立即登录
                        </Button>
                        <div className="mt5">
                            <a className="login-form-forgot" onClick={() => this.onChangeIsMobile(false)}>
                                账号登录
                            </a>
                        </div>
                    </Form.Item>
                </Form>
            );
        }

        render(): React.ReactNode {
            return this.renderLoginContentBox();
        }
    }

    export const Page = template(Component, (state) => state[Namespaces.login]);
}
